import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { BookingApi } from "../../API/Apis/BookingApi";
import { t } from "i18next";
import BookingDetalisListSideNavigation from "../../components/Layouts/ReservationsCard/BookingDetalisListSideNavigation";
import createTheme from "@mui/material/styles/createTheme";
import type { Theme } from "@mui/material/styles";
import { formatThemeForReservedArea } from "../../config/MuiColorThemes";
import CollapsibleNavbar from "../../components/Layouts/ReservedAreaDashboard/CollapseNavbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import { AppStandardButton } from "../../components/UI/AppButtons";
import { requestParentResize } from "../../helpers/iframeSource";
import * as yup from "yup";
import owasp from "owasp-password-strength-test";

interface PasswordState {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const PasswordChangeDialog = ({
  open,
  message,
  onClose,
}: {
  open: boolean;
  message: string;
  onClose: () => void;
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    aria-labelledby="alert-dialog-title"
    aria-describedby="alert-dialog-description"
    PaperProps={{ style: { borderRadius: "20px" } }}
  >
    <DialogTitle style={{ padding: "3rem" }}>{message}</DialogTitle>
    <DialogActions style={{ justifyContent: "center" }}>
      <AppStandardButton variant="outlined" onClick={onClose}>
        <p className="text-black normal-case">{t("Close")}</p>
      </AppStandardButton>
    </DialogActions>
  </Dialog>
);

owasp.config({
  allowPassphrases: false,
  minLength: 8,
  minOptionalTestsToPass: 4,
});
const PasswordInputField = ({
  label,
  name,
  value,
  onChange,
  width,
  isSmallScreen,
  error,
  helperText,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  width: string;
  isSmallScreen: boolean;
  error?: boolean;
  helperText?: string;
}) => (
  <>
    <div style={{ marginBottom: "-9px" }}>
      <label
        style={{
          color: "#000000",
          fontFamily: "MBCorpoSTitle-CondRegular-Web-new",
        }}
      >
        {t(label)}
      </label>
    </div>
    <TextField
      name={name}
      type="password"
      margin="normal"
      required
      value={value}
      error={error}
      helperText={helperText}
      style={{ width: isSmallScreen ? "100%" : width }}
      InputProps={{
        sx: {
          fontFamily: "MBCorpoSTitle-CondRegular-Web-new",
          fontSize: "12px",
        },
      }}
      InputLabelProps={{
        sx: {
          "&.MuiInputLabel-root": {
            fontFamily: "MBCorpoSTitle-CondRegular-Web-new",
          },
        },
      }}
      label={t(label)}
      onChange={onChange}
    />
  </>
);

export default function ChangePassword() {
  const [dynamicTheme, setDynamicTheme] = useState<Theme | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const isDesktopScreen = window.innerWidth >= 1310;
  const isSmallScreen = window.innerWidth <= 768;
  // const [userId] = useState(localStorage.getItem("userID"));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [password, setPassword] = useState<PasswordState>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Partial<PasswordState>>({});

  useEffect(() => {
    async function fetchTheme() {
      const dynamicPalette = await formatThemeForReservedArea();
      setDynamicTheme(createTheme(dynamicPalette));
    }
    fetchTheme();
    requestParentResize(isDesktopScreen, 770, 630);
  }, [isDesktopScreen]);

  const handlePasswordChange =
    (field: keyof PasswordState) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPassword((prev) => ({ ...prev, [field]: event.target.value }));
    };
  const passwordSchema = yup
    .string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[^A-Za-z0-9]/,
      "Password must contain at least one special character"
    );

  const handleSavePassword = async () => {
    const newErrors: Partial<PasswordState> = {};

    if (!password.oldPassword) {
      newErrors.oldPassword = "Old password is required";
    }

    try {
      await passwordSchema.validate(password.newPassword);
    } catch (error: any) {
      newErrors.newPassword = error.message;
    }

    if (password.newPassword !== password.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    const strength = owasp.test(password.newPassword);

    if (!strength.strong) {
      newErrors.newPassword = strength.errors.join(" "); // or show just result.errors[0] for one message
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    try {
      const response = await BookingApi.changePassword(password);
      setModalMessage(
        response.status === "success"
          ? "Password updated successfully"
          : response.errorMessage
      );
      setOpenModal(true);
    } catch (error) {
      setModalMessage("An error occurred while changing password");
      setOpenModal(true);
    }
  };

  const toggleDrawer = (isOpen: boolean) => {
    setIsDrawerOpen(isOpen);
  };

  return (
    <div>
      <div style={{ color: "white", display: "none" }}>
        <CollapsibleNavbar
          isDrawerOpen={isDrawerOpen}
          toggleDrawer={toggleDrawer}
        />
      </div>

      <div className="flex">
        {!isSmallScreen && (
          <div className="!w-780" style={{ width: "300px" }}>
            <BookingDetalisListSideNavigation />
          </div>
        )}

        <div style={{ width: isSmallScreen ? "352px" : "" }}>
          <div
            className="text-[#333] mb-4"
            style={{
              width: "90%",
              marginLeft: !isSmallScreen ? "13%" : "1rem",
              marginTop: "43px",
              paddingTop: "16px",
            }}
          >
            <h1
              className="font-medium text-[28px] text-[#232323]"
              style={{
                fontFamily: "MBCorpoAScreen1Cond-Regular-Web",
                fontSize: isSmallScreen ? "24px" : "36px",
              }}
            >
              {t("Change Password")}
            </h1>
            <h4
              className="text-[#838181] font-normal text-[18px]"
              style={{
                fontFamily: "MBCorpoSTitle-CondRegular-Web-new",
                fontSize: isSmallScreen ? "14px" : "16px",
              }}
            >
              {t("You can change your password from here")}
            </h4>
          </div>

          <div
            className="container p-8 sm:shadow-md md:shadow-lg lg:shadow-xl"
            style={{
              margin: "19px",
              paddingTop: "0px",
              padding: isSmallScreen ? "0px" : "",
            }}
          >
            <div className="flex flex-col">
              <div className="justify-between space-x-2 ml:0 md:ml-[20px]">
                <PasswordInputField
                  label="Old Password"
                  name="oldPassword"
                  value={password.oldPassword}
                  onChange={handlePasswordChange("oldPassword")}
                  width="418px"
                  isSmallScreen={isSmallScreen}
                  error={!!errors.oldPassword}
                  helperText={errors.oldPassword}
                />

                <PasswordInputField
                  label="New Password"
                  name="newPassword"
                  value={password.newPassword}
                  onChange={handlePasswordChange("newPassword")}
                  width="418px"
                  isSmallScreen={isSmallScreen}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword}
                />

                <PasswordInputField
                  label="Confirm Password"
                  name="confirmPassword"
                  value={password.confirmPassword}
                  onChange={handlePasswordChange("confirmPassword")}
                  width="418px"
                  isSmallScreen={isSmallScreen}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                />
                <div
                  className="flex gap-4 flex-col md:flex-row"
                  style={{ width: isSmallScreen ? "100%" : "" }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    onClick={handleSavePassword}
                    sx={{
                      gap: 3,
                      fontWeight: "bold",
                      textTransform: "capitalize",
                      fontFamily: "MBCorpoSText-Regular-Web-new",
                      fontSize: "12px",
                      bgcolor: dynamicTheme?.palette?.["primary"]?.main,
                    }}
                  >
                    {t("Save")}
                  </Button>
                  <Button
                    type="submit"
                    variant="outlined"
                    sx={{
                      gap: 3,
                      fontWeight: "bold",
                      borderColor: "#7B7B7B",
                      fontFamily: "MBCorpoSText-Regular-Web-new",
                      fontSize: "12px",
                      textTransform: "capitalize",
                    }}
                    style={{ color: "#7B7B7B" }}
                  >
                    {t("Cancel")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PasswordChangeDialog
        open={openModal}
        message={modalMessage}
        onClose={() => {
          setOpenModal(false);
          setModalMessage("");
        }}
      />
    </div>
  );
}
